import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
// ~/stores/user.ts
import { defineStore } from 'pinia'

export const useUserStore = defineStore('user', () => {
  const user = ref<any>(false)
  const access_token = ref<string | false>(false)
  const authChecked = ref(false)

  // Helper function to get cookie value
  function getCookie(name: string): string | null {
    if (!import.meta.client)
      return null
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2)
      return parts.pop()?.split(';').shift() || null
    return null
  }

  // Helper function to set cookie
  function setCookie(name: string, value: string, days: number = 30) {
    if (!import.meta.client)
      return
    const expires = new Date()
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000))
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`
  }

  // Helper function to remove cookie (must match setCookie path + SameSite or some UAs keep the old cookie)
  function removeCookie(name: string) {
    if (!import.meta.client)
      return
    const expired = 'Thu, 01 Jan 1970 00:00:00 GMT'
    document.cookie = `${name}=;expires=${expired};path=/;SameSite=Lax`
    document.cookie = `${name}=;Max-Age=0;path=/;SameSite=Lax`
  }

  // Initialize user from Capacitor Preferences and cookies as fallback (web only)
  async function initializeUser() {
    if (!import.meta.client)
      return

    const isNative = Capacitor.isNativePlatform()

    try {
      const userData = await Preferences.get({ key: 'user' })
      const tokenData = await Preferences.get({ key: 'token' })

      if (tokenData.value) {
        access_token.value = tokenData.value
      }

      if (userData.value) {
        if (tokenData.value) {
          user.value = JSON.parse(userData.value)
        }
        else {
          // Déconnexion partielle ou prefs corrompues : ne pas garder un profil sans jeton
          try {
            await Preferences.remove({ key: 'user' })
          }
          catch {}
          user.value = false
        }
      }

      // Apps natives : le jeton vit dans Preferences uniquement. Un cookie `token` résiduel
      // (WebView) après logout ne doit pas réactiver une session au prochain cold start.
      if (isNative) {
        if (!tokenData.value)
          removeCookie('token')
      }
      else if (!tokenData.value) {
        const cookieToken = getCookie('token')
        if (cookieToken)
          access_token.value = decodeURIComponent(cookieToken)
      }
    }
    catch (error) {
      console.error('Error initializing user from Preferences:', error)

      if (isNative) {
        removeCookie('token')
        return
      }

      try {
        const cookieToken = getCookie('token')
        if (cookieToken)
          access_token.value = decodeURIComponent(cookieToken)
      }
      catch (cookieError) {
        console.error('Error initializing token from cookies:', cookieError)
      }
    }
  }

  async function setUser(userData: any) {
    user.value = userData
    if (import.meta.client) {
      try {
        // Save to Preferences (for mobile apps)
        await Preferences.set({
          key: 'user',
          value: JSON.stringify(userData),
        })
      }
      catch (error) {
        console.error('Error saving user to Preferences:', error)
      }
    }
  }

  async function setToken(token: any) {
    access_token.value = token
    if (import.meta.client) {
      try {
        // Save to Preferences (for mobile apps)
        await Preferences.set({
          key: 'token',
          value: token,
        })
      }
      catch (error) {
        console.error('Error saving token to Preferences:', error)
      }

      try {
        // Also save to cookies (for web compatibility)
        setCookie('token', encodeURIComponent(token))
      }
      catch (error) {
        console.error('Error saving token to cookies:', error)
      }
    }
  }

  async function clearUser() {
    user.value = false
    access_token.value = false

    if (import.meta.client) {
      try {
        await Preferences.remove({ key: 'user' })
        await Preferences.remove({ key: 'token' })
      }
      catch (error) {
        console.error('Error clearing user from Preferences:', error)
      }

      try {
        removeCookie('token')
      }
      catch (error) {
        console.error('Error clearing token from cookies:', error)
      }
    }
  }

  async function refreshUser(url: string) {
    console.log('refresh')
    try {
      // If no token in memory, try to get it from cookies as fallback
      if (!access_token.value) {
        const cookieToken = getCookie('token')
        if (cookieToken) {
          access_token.value = decodeURIComponent(cookieToken)
        }
        else {
          return
        }
      }

      const response = await $fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${access_token.value}`,
        },
      })

      if (response && response.success === 1) {
        await setUser(response.user_data)
        await setToken(response.access_token)
      }
      else {
        await clearUser()
        console.warn('User logged out on refresh')
        // Redirect to home page
        if (import.meta.client) {
          window.location.href = '/'
        }
      }
    }
    catch (err: any) {
      console.error('Refresh error:', err)
      await clearUser()
      // Check if the error is related to an invalid refresh token
      if (err.response && err.response.status === 401) {
        // Redirect to home page
        if (import.meta.client) {
          window.location.href = '/'
        }
      }
    }
  }

  return {
    user,
    access_token,
    authChecked,
    setUser,
    refreshUser,
    setToken,
    clearUser,
    initializeUser,
  }
})
